import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Download, FileText, Check, Loader2, Lock, Video, ArrowUpRight } from "lucide-react";
import { Reveal, MaskLines, MaskLinesInView } from "@/components/Reveal";
import CTASection from "@/components/CTASection";
import { RESOURCES, DOWNLOADS } from "@/lib/data";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BRIEF_BY_RESOURCE = {
  "golden-moment": "golden",
  "six-minute-sale": "sixmin",
};

export default function Resources() {
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", years_in_business: "", website: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const openFor = (dl) => {
    setActive(dl);
    setForm({ name: "", email: "", years_in_business: "", website: "" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please add your name and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/lead`, { ...form, resource: active.title });
      setActive(null);
      setForm({ name: "", email: "", years_in_business: "", website: "" });
      toast.success("Thanks, your request is in. Scott will email your copy shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please call 575-420-3926.");
    } finally {
      setLoading(false);
    }
  };

  const FIELD =
    "w-full bg-transparent border-0 border-b border-blue/25 py-3 font-body text-blue placeholder:text-slate/60 focus:outline-none focus:border-amber-deep transition-colors";

  return (
    <>
      <section className="bg-cream grain pt-40 pb-20 md:pt-52 md:pb-28 relative">
        <div className="container-editorial relative z-10 max-w-4xl">
          <Reveal>
            <p className="eyebrow text-amber-deep mb-8 flex items-center gap-3">
              <span className="w-10 h-px bg-amber-deep" /> Resources
            </p>
          </Reveal>
          <h1 className="font-serif font-bold text-5xl md:text-7xl lg:text-8xl text-blue leading-[0.98] tracking-tight">
            <MaskLines lines={["Ideas that build"]} delay={0.1} />
            <MaskLines lines={["toward the exit."]} className="italic text-amber-deep" delay={0.26} />
          </h1>
          <Reveal delay={0.5}>
            <p className="font-body text-lg text-charcoal/75 mt-8 max-w-2xl leading-relaxed">
              The thinking behind the work. Two principles that guide every engagement, and a set of
              briefs you can take with you.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Concept chapters */}
      <section data-testid="resources-concepts" className="bg-cream pb-8 md:pb-16">
        <div className="container-editorial space-y-6">
          {RESOURCES.map((r, i) => {
            const dlKey = BRIEF_BY_RESOURCE[r.key];
            const dl = DOWNLOADS.find((d) => d.key === dlKey);
            return (
              <Reveal key={r.key} delay={i * 0.08}>
                <div className="relative grid md:grid-cols-[0.5fr_0.5fr] gap-8 md:gap-14 p-10 md:p-16 border border-blue/12 bg-cream overflow-hidden">
                  <span className="absolute -top-4 right-2 md:right-8 font-serif font-bold text-[7rem] md:text-[11rem] leading-none text-amber/10 select-none pointer-events-none z-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="relative z-10">
                    <p className="eyebrow text-amber-deep mb-5">{r.label}</p>
                    <h2 className="font-serif font-bold text-4xl md:text-5xl text-blue leading-tight">
                      {r.title} <span className="italic text-amber-deep">{r.titleItalic}</span>
                    </h2>
                  </div>
                  <div className="relative z-10">
                    <p className="font-body text-lg text-charcoal/75 leading-relaxed">{r.body}</p>
                    <ul className="mt-6 space-y-3">
                      {r.points.map((p, j) => (
                        <li key={j} className="flex items-start gap-3 font-body text-charcoal/80">
                          <Check size={17} className="text-amber-deep mt-1 shrink-0" /> {p}
                        </li>
                      ))}
                    </ul>
                    {dl && (
                      <button
                        onClick={() => openFor(dl)}
                        data-testid={`brief-link-${dl.key}`}
                        className="mt-7 inline-flex items-center gap-2 eyebrow text-[0.62rem] text-blue hover:text-amber-deep link-underline transition-colors"
                      >
                        <Download size={14} /> Request a free copy of the brief
                      </button>
                    )}
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* Downloads set */}
      <section data-testid="lead-magnet" className="bg-blue text-cream py-24 md:py-32 grain relative overflow-hidden">
        <div className="container-editorial relative z-10">
          <div className="max-w-3xl mb-14">
            <Reveal>
              <p className="eyebrow text-amber-bright mb-6 flex items-center gap-3">
                <span className="w-10 h-px bg-amber-bright" /> Free Power Briefs
              </p>
            </Reveal>
            <MaskLinesInView lines={["Take the thinking"]} className="font-serif font-bold text-4xl md:text-6xl text-cream leading-[1.03]" />
            <MaskLinesInView lines={["with you."]} className="font-serif font-bold italic text-4xl md:text-6xl text-amber-bright leading-[1.03] mt-1" />
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {DOWNLOADS.map((dl, i) => (
              <Reveal key={dl.key} delay={i * 0.08}>
                <div className="relative flex flex-col h-full bg-blue-deep border border-amber/25 p-8 md:p-9">
                  {dl.type === "demo" ? (
                    <Video size={34} className="text-amber-bright mb-6" />
                  ) : (
                    <FileText size={34} className="text-amber-bright mb-6" />
                  )}
                  <p className="eyebrow text-amber-bright text-[0.56rem] mb-3">{dl.eyebrow}</p>
                  <h3 className="font-serif font-bold text-2xl md:text-3xl text-cream leading-tight">
                    {dl.headline}
                  </h3>
                  <p className="font-body text-sm text-cream/70 mt-4 leading-relaxed flex-1">
                    {dl.description}
                  </p>

                  {dl.type === "demo" ? (
                    <>
                      <Link
                        to="/contact"
                        state={{
                          interest: "Live power brief demo",
                          prefill: `I'd like to request a free 15-minute live power brief demo of "${dl.title}" over Google Meet or Zoom. My availability is: `,
                        }}
                        data-testid={`demo-request-${dl.key}`}
                        className="group relative overflow-hidden inline-flex items-center justify-center gap-2 bg-amber-deep text-cream px-7 py-3.5 eyebrow text-[0.62rem] mt-7 w-full"
                      >
                        <span className="relative z-10 flex items-center gap-2">
                          <Video size={15} /> Request a 15-min live demo
                        </span>
                        <span className="absolute inset-0 bg-cream translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]" />
                        <span className="absolute inset-0 flex items-center justify-center gap-2 text-blue opacity-0 group-hover:opacity-100 transition-opacity duration-300 delay-150 eyebrow text-[0.62rem] z-20 pointer-events-none">
                          <Video size={15} /> Request a 15-min live demo
                        </span>
                      </Link>
                      <p className="eyebrow text-[0.5rem] text-cream/40 mt-3 flex items-center gap-2">
                        <Video size={10} /> Free · 15 minutes · Google Meet or Zoom
                      </p>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => openFor(dl)}
                        data-testid={`download-open-${dl.key}`}
                        className="group relative overflow-hidden inline-flex items-center justify-center gap-2 bg-amber-deep text-cream px-7 py-3.5 eyebrow text-[0.62rem] mt-7 w-full"
                      >
                        <span className="relative z-10 flex items-center gap-2">
                          <Download size={15} /> Request a copy
                        </span>
                        <span className="absolute inset-0 bg-cream translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]" />
                        <span className="absolute inset-0 flex items-center justify-center gap-2 text-blue opacity-0 group-hover:opacity-100 transition-opacity duration-300 delay-150 eyebrow text-[0.62rem] z-20 pointer-events-none">
                          <Download size={15} /> Request a copy
                        </span>
                      </button>
                      <p className="eyebrow text-[0.5rem] text-cream/40 mt-3 flex items-center gap-2">
                        <Lock size={10} /> A name and email, and Scott emails your copy
                      </p>
                    </>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
        <DialogContent data-testid="lead-dialog" className="bg-cream border-blue/20 sm:max-w-md">
          <DialogHeader>
            <p className="eyebrow text-amber-deep text-[0.58rem] mb-1">Request a Copy</p>
            <DialogTitle className="font-serif font-bold text-3xl text-blue leading-tight">
              {active?.title}
            </DialogTitle>
            <DialogDescription className="font-body text-charcoal/70 pt-1">
              Tell us where to send it, and Scott will email your copy shortly.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} data-testid="lead-form" className="space-y-6 pt-2">
            <div>
              <label className="eyebrow text-[0.56rem] text-slate block mb-2">Name *</label>
              <input data-testid="lead-name" className={FIELD} value={form.name} onChange={update("name")} placeholder="Your full name" />
            </div>
            <div>
              <label className="eyebrow text-[0.56rem] text-slate block mb-2">Email *</label>
              <input data-testid="lead-email" type="email" className={FIELD} value={form.email} onChange={update("email")} placeholder="you@company.com" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="eyebrow text-[0.56rem] text-slate block mb-2">Years in business</label>
                <input data-testid="lead-years" className={FIELD} value={form.years_in_business} onChange={update("years_in_business")} placeholder="e.g. 8" />
              </div>
              <div>
                <label className="eyebrow text-[0.56rem] text-slate block mb-2">Website</label>
                <input data-testid="lead-website" className={FIELD} value={form.website} onChange={update("website")} placeholder="company.com" />
              </div>
            </div>
            <button
              type="submit"
              data-testid="lead-submit"
              disabled={loading}
              className="group relative overflow-hidden w-full bg-blue text-cream px-8 py-4 eyebrow text-xs flex items-center justify-center gap-2 disabled:opacity-60"
            >
              <span className="relative z-10 flex items-center gap-2">
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                {loading ? "Sending…" : "Send my request"}
              </span>
              <span className="absolute inset-0 bg-amber-deep translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]" />
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <CTASection />
    </>
  );
}
